import { motion } from "framer-motion";
import EmptyIcon from "assets/icons/admin-view/EmptyIcon";
import OrDivision from "components/main-view/utils/components/OrDivision";
import { useState } from "react";
import { MdAddCircleOutline } from "react-icons/md";
import SearchAndCardsList from "components/main-view/utils/pop-up-panels/add-contact-pop-up/SearchAndCardsList";
import {
  ContactListType,
  ParentListType,
  SchoolListType,
  defaultContact,
} from "utils/interfaces";
import ConfirmPrimaryPopUp from "components/main-view/utils/pop-up-panels/common/ConfirmPrimaryPopUp";
import SuccessPopUpMessage from "components/main-view/utils/pop-up-panels/common/SuccessPopUpMessage";
import BackButtonHeaderPopUp from "../common/BackButtonHeader";
// import CreateNewContact from "../common/CreateNewUser";

interface AddParentPanelProps {
  contextSchool?: SchoolListType | null;
  selectedData?: ParentListType;
  setShowPopUp: React.Dispatch<React.SetStateAction<boolean>>;
  selectedContacts: ContactListType[];
  setSelectedContacts: React.Dispatch<React.SetStateAction<ContactListType[]>>;
  getData?: () => Promise<any>;
  setSelectedData?: (value: any) => void;
}

/**
 * Definition of the variants of this component
 */
const variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: {
    opacity: 0,
    transition: { duration: 0.1, ease: "easeOut" },
  },
};

const AddParentPanel: React.FC<AddParentPanelProps> = ({
  contextSchool,
  selectedData,
  setShowPopUp,
  selectedContacts,
  setSelectedContacts,
  getData,
  setSelectedData,
}) => {
  // States definition
  const [numberSearch, setNumberSearch] = useState("");
  const [createNewParent, setCreateNewParent] = useState(!!selectedData);
  const [showSuccessPopUp, setShowSuccessPopUp] = useState(false);
  const [showConfirmPopUp, setShowConfirmPopUp] = useState(false);
  const [lastSelectedContact, setLastSelectedContact] =
    useState<ContactListType>(defaultContact);
  const [isLoadingData, setIsLoadingData] = useState(true);

  /**
   * Definition of the function when pressed back button
   */
  const handlePressBack = () => {
    // If create new was pressed, then come back to the regular option
    if (createNewParent) {
      // If we are editing a parent, then close the pop up once clicked
      // the press button
      if (selectedData) setShowPopUp(false);
      else setCreateNewParent(false);
    }
    // Otherwise, close the pop up
    else setShowPopUp(false);
  };

  /**
   * Definition of the function executed after creating a parent
   * in the database
   */
  const afterCreateParent = () => {
    // If editing, just close the pop up
    if (selectedData) {
      // Close the pop up
      setShowPopUp(false);
      // And refresh the list of teachers
      if (getData) getData();
    } else {
      // Come back to the regular view
      setCreateNewParent(false);
      // And display the confirm pop up
      // And display the confirm primary contact pop up
      if (selectedContacts.length > 0) {
        setShowConfirmPopUp(true);
      }
    }
  };

  /**
   * Function executed when clicking "add as primary contact" on the
   * ConfirmPrimaryPopUp panel
   */
  const addAsPrimaryContact = () => {
    // Put the last selected contact at first position to be the primary parent,
    // but double check that the selected contacts has a minimum of elements to
    // perform it
    if (selectedContacts.length > 1) {
      setSelectedContacts(
        selectedContacts.slice(-1).concat(selectedContacts.slice(0, -1))
      );
    }

    // Close the pop up
    setShowConfirmPopUp(false);

    // And display the pop up successful message
    setShowSuccessPopUp(true);
  };

  /**
   * Function executed when clicking "add as regular contact" on the
   * ConfirmPrimaryPopUp panel
   */
  const addAsAdditionalContact = () => {
    // Just close the pop up
    setShowConfirmPopUp(false);

    // And display the pop up successful message
    setShowSuccessPopUp(true);
  };

  return (
    <>
      <motion.div
        className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-20 flex flex-col flex-grow"
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
        onClick={() => setShowPopUp(false)}
        onAnimationComplete={(definition) => {
          if (definition === "exit" && setSelectedData) {
            setSelectedData(null);
          }
        }}
      />
      <motion.div
        className="fixed w-[455px] h-[793px] max-h-[90%] left-0 right-0 ml-auto mr-auto p-6 bottom-5 pb-8 bg-white rounded-3xl flex-col justify-start items-start gap-6 inline-flex z-30"
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
      >
        <div
          className={`w-full h-full flex-col justify-start items-start inline-flex relative ${
            createNewParent ? "gap-8" : "gap-6"
          }`}
        >
          <div className="self-stretch justify-between items-center inline-flex">
            <div className="justify-start items-center gap-3 flex">
              <BackButtonHeaderPopUp onClick={handlePressBack} />
              <div className="text-neutral-600 text-lg font-normal font-sans leading-snug">
                {selectedData
                  ? "Edit Parent/Guardian"
                  : createNewParent
                  ? "Create new Parent/Guardian"
                  : "Add Parent/Guardian"}
              </div>
            </div>
            {/* Hide create new button when creating parent */}
            {!createNewParent && (
              <button
                className="group rounded justify-center items-center gap-2 flex cursor-pointer"
                onClick={() => setCreateNewParent(true)}
              >
                <div className="w-6 h-6 relative">
                  <MdAddCircleOutline className="w-full h-full fill-secondary-600 group-hover:fill-secondary-700" />
                </div>
                <div className="text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans leading-snug">
                  Create new
                </div>
              </button>
            )}
          </div>
          {/* Regular view when not creating parents */}
          {!createNewParent && (
            <div
              className={
                numberSearch || selectedContacts.length > 0
                  ? "w-full h-full flex-col justify-start items-start gap-8 inline-flex overflow-hidden"
                  : "w-full h-full flex-col justify-center items-center gap-8 inline-flex overflow-hidden"
              }
            >
              {
                <>
                  {!numberSearch &&
                    !isLoadingData &&
                    selectedContacts.length === 0 && (
                      <>
                        <div className="self-stretch h-fit flex-col justify-start items-center gap-10 flex">
                          <div className="w-[244px] h-[183px] relative">
                            <EmptyIcon width="244" height="183" />
                          </div>
                          <div className="self-stretch h-fit flex-col justify-start items-center gap-3 flex">
                            <div className="self-stretch text-center text-neutral-600 text-lg font-semibold font-sans leading-snug">
                              This area is empty
                            </div>
                            <div className="self-stretch text-center text-neutral-700 text-sm font-normal font-sans leading-[17px]">
                              Just click on the "Create New" button, and let's
                              get started
                            </div>
                          </div>
                        </div>
                        <OrDivision />
                      </>
                    )}
                  <SearchAndCardsList
                    contextSchool={contextSchool}
                    role="parent"
                    numberSearch={numberSearch}
                    showSuccessPopUp={showSuccessPopUp}
                    lastSelectedContact={lastSelectedContact}
                    isLoadingData={isLoadingData}
                    setNumberSearch={setNumberSearch}
                    selectedContacts={selectedContacts}
                    setSelectedContacts={setSelectedContacts}
                    setShowSuccessPopUp={setShowSuccessPopUp}
                    setLastSelectedContact={setLastSelectedContact}
                    setShowConfirmPopUp={setShowConfirmPopUp}
                    setIsLoadingData={setIsLoadingData}
                  />
                </>
              }
            </div>
          )}
          {/* {createNewParent && (
            <CreateNewContact
              role="parent"
              // contextSchool={contextSchool}
              contactToEdit={selectedData as ContactListType}
              contacts={selectedContacts}
              confirmButtonText={
                selectedData ? "Save Parent/Guardian" : "Create Parent/Guardian"
              }
              // Cancel the primary option when editing a parent
              showPrimaryOption={!selectedData}
              setContacts={setSelectedContacts}
              afterCreateEditContact={afterCreateParent}
              setLastSelectedContact={setLastSelectedContact}
            />
          )} */}
          {showConfirmPopUp && (
            <ConfirmPrimaryPopUp
              title="Add as Primary contact"
              paragraphs={[
                "Would you like to add this contact as the primary parent/guardian for this student?",
                "This can be changed later in the settings, as you add additional contacts to this organisation.",
              ]}
              options={[
                {
                  text: "Add as primary contact",
                  onClick: addAsPrimaryContact,
                },
                {
                  text: "Add as an additional contact",
                  onClick: addAsAdditionalContact,
                },
              ]}
              handleClosePopUp={() => setShowConfirmPopUp(false)}
            />
          )}
          <SuccessPopUpMessage
            name={
              lastSelectedContact.firstName + " " + lastSelectedContact.lastName
            }
            nameType="Parent/Guardian"
            showSuccessPopUp={showSuccessPopUp}
            setShowSuccessPopUp={setShowSuccessPopUp}
            dismissAction={() => {}}
          />
        </div>
      </motion.div>
    </>
  );
};

export default AddParentPanel;
